import { ICommonSettings } from "@kuika/kuika-cl-model";
import { IGoogleRecaptchaSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IGoogleRecaptchaSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IGoogleRecaptchaSettings;
}

export interface IGoogleRecaptchaSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingRecaptchaId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class GoogleRecaptchaSettings extends React.PureComponent<
  IGoogleRecaptchaSettingsProps,
  IGoogleRecaptchaSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IGoogleRecaptchaSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IGoogleRecaptchaSettingsProps & IWithSDProps) {
    if (prevProps.editingRecaptchaId !== this.props.editingRecaptchaId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingRecaptchaId) return;

    const editingConfig = this.props.commonSettings.googleRecaptchaSettings?.find(
      (x) => x.id === this.props.editingRecaptchaId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const googleRecaptchaSettings = this.props.commonSettings?.googleRecaptchaSettings;
    if (!googleRecaptchaSettings) return;

    const isSettingAlreadyExist = googleRecaptchaSettings.some(
      (x) => x.name === value && x.id !== this.props.editingRecaptchaId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Google Recaptcha Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGoogleRecaptchaSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      secretKey: data.secretKey?.trim(),
      siteKey: data.siteKey?.trim()
    };
    if (this.props.editingRecaptchaId) {
      obj.id = this.props.editingRecaptchaId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleRecaptchaSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleRecaptchaSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingRecaptchaId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingRecaptchaId || "",
      configKey: "googleRecaptchaSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Recaptcha setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Recaptcha setting"
        secondHeader="Google Recaptcha setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingRecaptchaId ? this.deleteSetting : undefined}
            deleteTestId="googleRecaptchaSettingDeleteButton"
            submitTestId="googleRecaptchaSettingCreateButton"
            submitLabel={this.props.editingRecaptchaId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleRecaptchaForm"
          />
        }
      >
        <Form
          id="configGoogleRecaptchaForm"
          name="googleRecaptchaSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_googlerecaptchasettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_googlerecaptchasettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Google Recaptcha Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Google Recaptcha Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="googleRecaptchaSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Site Key" required tooltipContentKey="appsettings_googlerecaptchasettingpop_up_sitekey">
              <Form.Item name="siteKey" rules={[{ required: true, message: "Please enter a Site Key." }]}>
                <KuikaInput size="md" data-testid="googleRecaptchaSettingSiteKeyInput" />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Secret Key"
              required
              tooltipContentKey="appsettings_googlerecaptchasettingpop_up_secretkey"
            >
              <Form.Item name="secretKey" rules={[{ required: true, message: "Please enter a Secret Key." }]}>
                <KuikaInput size="md" data-testid="googleRecaptchaSettingSecretKeyInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
